import {
  AtlasMenuContentItem,
  Button,
  ButtonGroup,
  getColumnSortHeadCellProps,
  getRowInteractionRowProps,
  getSelectionColumn,
  Menu,
  Table,
} from "@resource/atlas";
import { useFlags } from "@resource/client-ffs";
import { useAuthContext } from "auth/context";
import BulkEditStageTemplateInstallationsModal from "components/BulkEdit/EditStageTemplateInstallations/BulkEditStageTemplateInstallationsModal";
import FakeMultiSelect from "components/FakeMultiSelect";
import UnconfiguredJobsBanner from "components/JobPage/UnconfiguredJobsBanner";
import { FilterOption } from "components/legacy/AutocompleteFilter";
import BulkEditInterviewKitsModal from "components/legacy/BulkEdit/EditInterviewKits/BulkEditInterviewKitsModal";
import { BulkStageEditMode } from "components/legacy/BulkEdit/EditStageTemplateInstallations/types";
import { Pagination } from "components/Pagination/Pagination";
import GlobalFilter from "components/Table/GlobalFilter";
import usePagination from "components/Table/hooks/usePagination";
import useQueryParamsState from "components/Table/hooks/useQueryParamState";
import useRowSelect from "components/Table/hooks/useRowSelect";
import NoTableResults from "components/Table/NoTableResults";
import FeatureFlags from "generated/FeatureFlags";
import {
  GetOpenJobs_openJobs as Job,
  JobRoleGuideTemplateSetupStatus,
  JourneyStatusEnum,
  OrderByArg,
} from "generated/schemaTypes";
import _, { some } from "lodash";
import router from "next/router";
import { useEffect, useMemo, useState } from "react";
import { analytics } from "react-hooks/useAnalytics";
import { useEventCallback } from "react-hooks/useEventCallback";
import {
  Column,
  useFilters,
  useFlexLayout,
  useGlobalFilter,
  useResizeColumns,
  useSortBy,
  useTable,
  UseTableCellProps,
} from "react-table";
import { DefaultJourneyStatusFilter } from "utils/constants/defaults";

import BulkActionBar from "../BulkActionBar/BulkActionBar";
import ConfiguredStagesCell from "./ConfiguredStagesCell";
import GuideTemplateAssignmentAction from "./GuideTemplateAssignmentAction";
import JourneyNameCell from "./JourneyNameCell";
import SelectedFilters from "./SelectedFilters";
import SetupActionCell from "./SetupActionCell";

interface InitialVariables {
  limit: number;
  offset: number;
  search: string;
  atsUserIds: string[];
  atsDepartmentIds: string[];
  atsOfficeIds: string[];
  status: JourneyStatusEnum;
  orderBy: { [x: string]: string }[];
}

interface RefetchIds {
  search: string;
}

interface JourneyTableProps {
  journeys: Job[];
  refetchIds(refetchIds?: RefetchIds): Promise<void>;
  totalJourneysCount?: number | null;
  loading: boolean;
  initialVariables: InitialVariables;
  allJourneyToJobIdsMap: Record<string, string>;
  offices: FilterOption[];
  users: FilterOption[];
  departments: FilterOption[];
  atsType: string;
}

function JourneysTable({
  journeys,
  refetchIds,
  totalJourneysCount,
  loading,
  allJourneyToJobIdsMap,
  initialVariables,
  offices,
  departments,
  users,
  atsType,
}: JourneyTableProps) {
  const [editInterviewKitsModalOpen, setEditInterviewKitsModalOpen] =
    useState(false);
  const [editInterviewKitsModalMounted, setEditInterviewKitsModalMounted] =
    useState(false);
  const [editStagesModalMode, setEditStagesModalMode] =
    useState<BulkStageEditMode>();
  const [editStagesModalMounted, setEditStagesModalMounted] = useState(false);
  const {
    [FeatureFlags.USE_GUIDE_TEMPLATES]: useGuideTemplatesEnabled,
    [FeatureFlags.NEW_JOB_SETUP_FLOW]: jobSetupFlowEnabled,
    [FeatureFlags.ADD_STAGES_BULK_ACTION]: addStagesEnabled,
    [FeatureFlags.REMOVE_STAGES_BULK_ACTION]: removeStagesEnabled,
    [FeatureFlags.EDIT_INTERVIEWS_BULK_ACTION]: editInterviewsEnabled,
    [FeatureFlags.HIDE_INTERVIEWS_BULK_ACTION]: hideInterviewsEnabled,
    [FeatureFlags.EDIT_STAGES_BULK_ACTION]: editStagesEnabled,
  } = useFlags();
  const {
    zeusInfo: { isZeus, isZeusTransition },
  } = useAuthContext();

  const allJourneyIds = Object.keys(allJourneyToJobIdsMap);

  const columns = useMemo<Column<Job>[]>(
    () => [
      getSelectionColumn(),
      {
        Header: "Title",
        id: "name",
        width: 280,
        accessor: ({ name }: Job) => name,
        Cell: ({ row, value }: UseTableCellProps<Job>) => (
          <JourneyNameCell job={row.original} atsType={atsType} value={value} />
        ),
      },
      ...(!isZeus
        ? [
            {
              Header: "Stages",
              width: 80,
              id: "stageCount",
              accessor: ({ configuredStageCount }: Job) => configuredStageCount,
              Cell: ConfiguredStagesCell,
            },
            {
              Header: "Guides",
              width: 80,
              id: "sentGuideCount",
              Cell: ({ value }: UseTableCellProps<Job>) => (
                <p className="text-body-md">{value}</p>
              ),
              accessor: ({ sentGuideCount, setupStatus }: Job) =>
                setupStatus === JobRoleGuideTemplateSetupStatus.SETUP_COMPLETE
                  ? sentGuideCount || 0
                  : "",
            },
          ]
        : []),
      ...(jobSetupFlowEnabled && !isZeus
        ? [
            {
              id: "action",
              width: 80,
              Cell: SetupActionCell,
            },
          ]
        : []),
      {
        id: "department",
        width: 0,
        hidden: true,
      },
      {
        id: "office",
        width: 0,
        hidden: true,
      },
      {
        id: "user",
        width: 0,
        hidden: true,
      },
      {
        id: "status",
        width: 0,
        hidden: true,
      },
    ],
    [atsType, jobSetupFlowEnabled, isZeus]
  );

  const data = useMemo(() => journeys, [journeys]);
  const allIds = useMemo(() => allJourneyIds, [allJourneyIds]);
  const totalCount = useMemo(() => totalJourneysCount, [totalJourneysCount]);
  const {
    setFilter,
    getTableProps,
    getTableBodyProps,
    toggleAll,
    headerGroups,
    rows,
    setGlobalFilter,
    setPageSize,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: tableState,
    setAllFilters,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      allIds,
      totalCount,
      manualGlobalFilter: true,
      autoResetGlobalFilter: true,
      getRowId: ({ id }) => id,
      initialState: {
        hiddenColumns: ["user", "department", "status", "office"],
        globalFilter: initialVariables.search ?? "",
        filters: [
          {
            id: "department",
            value: initialVariables.atsDepartmentIds,
          },
          {
            id: "office",
            value: initialVariables.atsOfficeIds,
          },
          {
            id: "user",
            value: initialVariables.atsUserIds,
          },
          {
            id: "status",
            value: initialVariables.status,
          },
        ],
        pageIndex: initialVariables.offset / initialVariables.limit,
        pageSize: initialVariables.limit,
        sortBy: _(initialVariables.orderBy)
          .flatten()
          .map((value) => ({
            id: _(value).keys().head() as string,
            desc: _(value).values().head() === OrderByArg.desc,
          }))
          .value(),
        isLoading: loading,
      },
      autoResetSelectedRows: false,
      pageCount: 1,
      manualPagination: true,
      autoResetPage: false,
      manualSortBy: true,
      autoResetSortBy: false,
      manualFilters: true,
      autoResetFilters: false,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useQueryParamsState,
    useRowSelect,
    useResizeColumns,
    useFlexLayout
  );
  const { globalFilter, filters, pageSize, pageIndex, selectedRowIds } =
    tableState;
  const selectedIds = _(selectedRowIds).pickBy().keys().value();
  const selectedJobRoleIds = _.map(
    selectedIds,
    (id) => allJourneyToJobIdsMap[id]
  );
  const numSelectedRows = selectedIds.length;

  const selectedUsers = _.find(filters, { id: "user" })?.value;
  const selectedOffices = _.find(filters, { id: "office" })?.value;
  const selectedDepartments = _.find(filters, { id: "department" })?.value;
  const status =
    _.find(filters, { id: "status" })?.value ?? JourneyStatusEnum.ALL;

  const isFiltered =
    selectedUsers ||
    selectedOffices ||
    selectedDepartments ||
    globalFilter !== "" ||
    status !== DefaultJourneyStatusFilter;
  const clearFilters = () => {
    setGlobalFilter("");
    setAllFilters(() => [
      {
        id: "user",
        value: [],
      },
      {
        id: "office",
        value: [],
      },
      {
        id: "department",
        value: [],
      },
      {
        id: "status",
        value: DefaultJourneyStatusFilter,
      },
    ]);
  };

  const hasResults = !!totalJourneysCount;

  useEffect(() => {
    const updateData = async () => {
      if (numSelectedRows) {
        await refetchIds({
          search: globalFilter,
        });
      }
    };
    updateData();
  }, [globalFilter, refetchIds, numSelectedRows]);

  const statusFilterItems = useMemo(
    () =>
      [
        ["All", JourneyStatusEnum.ALL],
        ["Unconfigured", JourneyStatusEnum.UNCONFIGURED],
      ].map(
        ([name, option]): AtlasMenuContentItem => ({
          _type: "item",
          children: name,
          key: option,
          isSelectable: true,
          isSelected: status === option,
          onSelect: () => setFilter("status", option),
        })
      ),
    [setFilter, status]
  );

  const anyActionsEnabled = some([
    addStagesEnabled,
    removeStagesEnabled,
    editInterviewsEnabled,
    hideInterviewsEnabled,
    editStagesEnabled,
  ]);

  const onEditStages = useEventCallback(() => {
    setEditStagesModalMode(BulkStageEditMode.Edit);
    setEditStagesModalMounted(true);
  });

  const onAddStages = useEventCallback(() => {
    setEditStagesModalMode(BulkStageEditMode.Add);
    setEditStagesModalMounted(true);
  });

  const onRemoveStages = useEventCallback(() => {
    setEditStagesModalMode(BulkStageEditMode.Remove);
    setEditStagesModalMounted(true);
  });

  const onEditInterviews = useEventCallback(() => {
    setEditInterviewKitsModalOpen(true);
    setEditInterviewKitsModalMounted(true);
  });

  return (
    <div>
      {!isZeus && <UnconfiguredJobsBanner />}
      <div className="flex justify-start mt-5 w-full">
        <GlobalFilter
          placeholder="Search by name or Req Id"
          className="flex-shrink-0"
          globalFilter={globalFilter ?? ""}
          setGlobalFilter={setGlobalFilter}
        />
        <div className="flex space-x-4 flex-shrink-1 flex-grow px-4 items-center justify-start">
          <SelectedFilters
            label="Department"
            filterOptions={departments}
            filterValue={selectedDepartments}
            clearFilter={() => setFilter("department", [])}
          />
          <SelectedFilters
            label="Office"
            filterOptions={offices}
            filterValue={selectedOffices}
            clearFilter={() => setFilter("office", [])}
          />
          <SelectedFilters
            label="User"
            filterOptions={users}
            filterValue={selectedUsers}
            clearFilter={() => setFilter("user", [])}
          />
        </div>
        <ButtonGroup isGhost isDropdown>
          <FakeMultiSelect
            label="Department"
            minWidth="14rem"
            items={useMemo(
              () =>
                departments.map(
                  ({ label, id }): AtlasMenuContentItem => ({
                    _type: "item",
                    children: label,
                    key: id,
                    value: id,
                  })
                ),
              [departments]
            )}
            selectedIds={selectedDepartments}
            onSelectedIdsChange={(val) => setFilter("department", val)}
          />
          <FakeMultiSelect
            label="Office"
            minWidth="14rem"
            items={useMemo(
              () =>
                offices.map(
                  ({ label, id }): AtlasMenuContentItem => ({
                    _type: "item",
                    children: label,
                    key: id,
                    value: id,
                  })
                ),
              [offices]
            )}
            selectedIds={selectedOffices}
            onSelectedIdsChange={(val) => setFilter("office", val)}
          />
          <FakeMultiSelect
            label="User"
            minWidth="16rem"
            items={useMemo(
              () =>
                users.map(
                  ({ label, id }): AtlasMenuContentItem => ({
                    _type: "item",
                    children: label,
                    key: id,
                    value: id,
                  })
                ),
              [users]
            )}
            selectedIds={selectedUsers}
            onSelectedIdsChange={(val) => setFilter("user", val)}
          />
          <Menu.Root>
            <Menu.Trigger>
              <Button isGhost isDropdown>
                {_.capitalize(status ?? DefaultJourneyStatusFilter)}
              </Button>
            </Menu.Trigger>
            <Menu.Content items={statusFilterItems} />
          </Menu.Root>
        </ButtonGroup>
      </div>
      <Table.Root {...getTableProps()} className="mt-4">
        <Table.Head role="rowgroup">
          {_.map(headerGroups, (headerGroup) => (
            <Table.HeadRow {...headerGroup.getHeaderGroupProps()}>
              {_.map(headerGroup.headers, (column) => (
                <Table.HeadCell
                  {...column.getHeaderProps()}
                  {...getColumnSortHeadCellProps(column)}
                >
                  {column.render("Header")}
                </Table.HeadCell>
              ))}
            </Table.HeadRow>
          ))}
        </Table.Head>
        <Table.Body {...getTableBodyProps()}>
          {_.map(rows, (row) => {
            prepareRow(row);
            return (
              <Table.Row
                {...row.getRowProps()}
                {...getRowInteractionRowProps(
                  { state: tableState, row },
                  {
                    onClick: () => {
                      analytics.track("Jobs Page Job Clicked");
                      router.push(`/jobs/${row.original.id}`);
                    },
                  }
                )}
                className="h-16"
              >
                {row.cells.map((cell) => (
                  <Table.Cell {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          })}
          {!rows.length && (
            <NoTableResults
              loading={loading}
              hasResults={hasResults}
              isFiltered={isFiltered}
              clearFilters={clearFilters}
            />
          )}
        </Table.Body>
      </Table.Root>
      <BulkActionBar
        isVisible={!!numSelectedRows}
        selectedCount={numSelectedRows}
        totalCount={totalCount ?? 0}
        itemDisplayText="journey"
        isLoading={loading}
        onSelectAll={() => {
          analytics.track(
            "Journeys Table - Select All Journeys in BulkActionBar Clicked"
          );
          toggleAll(true);
        }}
        onClearAll={() => toggleAll(false)}
        onClose={() => toggleAll(false)}
      >
        <ButtonGroup>
          {/* Disable bulk guide template assignment for transition users since a given job could be v1 OR v2 */}
          {/* Can revisit and implement, but will take some refactoring to handle the transition case */}
          {useGuideTemplatesEnabled && !isZeusTransition && (
            <GuideTemplateAssignmentAction
              selectedIds={selectedIds}
              isZeus={isZeus}
            />
          )}
          {anyActionsEnabled && (
            <Menu.Root placement="top-start">
              <Menu.Trigger>
                <Button size="small">Bulk Actions</Button>
              </Menu.Trigger>
              <Menu.Content>
                {editStagesEnabled && (
                  <Menu.Item onSelect={onEditStages}>Edit Stages</Menu.Item>
                )}
                {addStagesEnabled && (
                  <Menu.Item onSelect={onAddStages}>Add Stages</Menu.Item>
                )}
                {removeStagesEnabled && (
                  <Menu.Item onSelect={onRemoveStages}>Remove Stages</Menu.Item>
                )}
                {(editInterviewsEnabled || hideInterviewsEnabled) && (
                  <Menu.Item onSelect={onEditInterviews}>
                    {editInterviewsEnabled
                      ? "Edit Interviews"
                      : "Hide Interviews"}
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Root>
          )}
        </ButtonGroup>
      </BulkActionBar>
      {hasResults ? (
        <Pagination
          totalCount={totalJourneysCount}
          pageSize={pageSize}
          pageIndex={pageIndex}
          isLoading={loading}
          onPrevPage={() => {
            analytics.track("Journeys Table - Page Changed", {
              direction: "previous",
            });
            previousPage();
          }}
          onNextPage={() => {
            analytics.track("Journeys Table - Page Changed", {
              direction: "next",
            });
            nextPage();
          }}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          onPageSizeChange={(newPageSize: number) => {
            analytics.track("Journeys Table - Page Size Changed", {
              target: newPageSize,
              previousValue: pageSize,
            });
            setPageSize(newPageSize);
          }}
        />
      ) : null}
      {editInterviewKitsModalMounted && (
        <BulkEditInterviewKitsModal
          open={editInterviewKitsModalOpen}
          onCancel={() => setEditInterviewKitsModalOpen(false)}
          onClose={() => setEditInterviewKitsModalOpen(false)}
          onExited={() => {
            setEditInterviewKitsModalMounted(false);
          }}
          jobRoleIds={selectedJobRoleIds}
        />
      )}{" "}
      {editStagesModalMounted && (
        <BulkEditStageTemplateInstallationsModal
          open={!_.isUndefined(editStagesModalMode)}
          mode={editStagesModalMode}
          onCancel={() => setEditStagesModalMode(undefined)}
          onClose={() => setEditStagesModalMode(undefined)}
          onExited={() => {
            setEditStagesModalMounted(false);
          }}
          jobRoleGuideTemplateIds={selectedIds}
        />
      )}
    </div>
  );
}

export default JourneysTable;
